import React, { createContext, useContext, useState, useEffect } from 'react';
import { CURRENCY_DECIMALS, SUPPORTED_CURRENCIES } from '../constants/currencyConstants';
import { useSelector } from 'react-redux';

const CurrencyContext = createContext();

/**
 * Currency Provider Component
 * Manages currency selection and provides formatting utilities
 */
export const CurrencyProvider = ({ children }) => {

  const { user } = useSelector((state) => state.auth);

  const [currency, setCurrency] = useState(user?.currency || 'USD');
  const [decimals, setDecimals] = useState(CURRENCY_DECIMALS[user?.currency] || 2);
  const [availableCurrencies, setAvailableCurrencies] = useState(Object.values(SUPPORTED_CURRENCIES));

  // Load currency from localStorage on mount and sync with user profile
  useEffect(() => {
    const savedCurrency = localStorage.getItem('selectedCurrency');
    const savedDecimals = localStorage.getItem('currencyDecimals');
    
    // Priority: 1. User Profile (if logged in), 2. LocalStorage, 3. Default (USD)
    if (user?.currency) {
      setCurrency(user.currency);
      // If user has a preferred currency, we should also try to set correct decimals
      // This might need a lookup if not in user object, but for now we rely on defaults/constants
      const userDecimals = CURRENCY_DECIMALS[user.currency] || 2;
      setDecimals(userDecimals);
    } else if (savedCurrency) {
      setCurrency(savedCurrency);
      if (savedDecimals) {
        setDecimals(parseInt(savedDecimals, 10));
      }
    }
  }, [user]); // Re-run when user changes (login/logout/update)

  // Update API meta when available (from API response)
  const updateCurrencyMeta = (meta) => {
    if (meta.currency) {
      setCurrency(meta.currency);
      localStorage.setItem('selectedCurrency', meta.currency);
    }
    
    if (meta.decimals !== undefined) {
      setDecimals(meta.decimals);
      localStorage.setItem('currencyDecimals', meta.decimals.toString());
    }
    
    if (meta.availableCurrencies) {
      setAvailableCurrencies(meta.availableCurrencies);
    }
  };

  // Manual currency change (for future currency selector UI)
  const changeCurrency = (newCurrency, newDecimals) => {
    setCurrency(newCurrency);
    setDecimals(newDecimals);
    localStorage.setItem('selectedCurrency', newCurrency);
    localStorage.setItem('currencyDecimals', newDecimals.toString());
  };

  const value = {
    currency,
    decimals,
    availableCurrencies,
    setCurrency: changeCurrency,
    updateCurrencyMeta
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

/**
 * Hook to access currency context
 */
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  
  return context;
};

export default CurrencyContext;